/**
 * The patient-side "Submitted — …" bubble for a submitted form.
 *
 * Values are masked at display only (egress), never at capture: an
 * NRIC-shaped value shows as S****999P. Empty values are skipped, and
 * `id_type` / `country_code` show only through the field they qualify.
 */

import { maskNric } from "./maskNric";

export interface FormAckOptions {
  /**
   * A field that holds an ID number of any type, masked by `id_type`: a
   * passport as `****` plus its last 4, which the NRIC mask would leave
   * whole.
   */
  idField?: string;
  /** Show keys as words ("id number" for `id_number`). */
  spacedKeys?: boolean;
}

export function buildFormAckSummary(
  data: Record<string, string>,
  options: FormAckOptions = {},
): string {
  const parts: string[] = [];
  const hiddenKeys = new Set(["id_type", "country_code"]);
  const idType = data["id_type"] || "NRIC";
  for (const [key, rawValue] of Object.entries(data)) {
    const value = rawValue ?? "";
    if (value.length === 0 || hiddenKeys.has(key)) continue;
    // Label the identification field based on the selected ID type
    let label = key;
    if (key === "nric") {
      if (idType === "Passport") label = "passport";
      else if (idType === "FIN") label = "fin";
    }
    if (options.spacedKeys) label = label.replace(/_/g, " ");
    const shown =
      key === options.idField && idType === "Passport"
        ? `****${value.trim().slice(-4)}`
        : maskNric(value);
    parts.push(`${label}: ${shown}`);
  }
  if (parts.length === 0) {
    return "Submitted.";
  }
  return `Submitted — ${parts.join(", ")}`;
}
